import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import EducatorDashboard from "@/pages/EducatorDashboard";
import StudentDashboard from "@/pages/StudentDashboard";
import AccountantDashboard from "@/pages/AccountantDashboard";
import FounderDashboard from "@/pages/FounderDashboard";
import FacilitatorDashboard from "@/pages/FacilitatorDashboard";
import GlobalLanding from "@/pages/GlobalLanding";
import SchoolLanding from "@/pages/SchoolLanding";
import PostsPage from "@/pages/posts/PostsPage";
import PostDetailPage from "@/pages/posts/PostDetailPage";
import GlobalDashboard from "@/pages/GlobalDashboard";
import ParentAccess from "@/pages/ParentAccess";
import ParentPortal from "@/pages/ParentPortal";
import PaySuccess from "@/pages/PaySuccess";
import PayCancel from "@/pages/PayCancel";
import OnboardWizard from "@/pages/school_onboarding/OnboardWizard";
import VendorOnboard from "@/pages/vendor_store/VendorOnboard";
import VendorDashboard from "@/pages/vendor_store/VendorDashboard";
import CreatorOnboard from "@/pages/creator_hub/CreatorOnboard";
import CreatorDashboard from "@/pages/creator_hub/CreatorDashboard";
import Storefront from "@/pages/storefront/Storefront";
import VendorPublic from "@/pages/storefront/VendorPublic";
import LiveTV from "@/pages/live_tv/LiveTV";
import ChannelPublic from "@/pages/live_tv/ChannelPublic";
import StudentKitStore from "@/pages/student_store/StudentKitStore";
import StudentPonderrTV from "@/pages/student_tv/StudentPonderrTV";
import OpenOnboardWizard from "@/pages/open_school/OpenOnboardWizard";
import OpenStudentConsole from "@/pages/open_school/OpenStudentConsole";
import FranchiseOnboard from "@/pages/franchise/FranchiseOnboard";
import LegalPage from "@/pages/legal/LegalPage";
import DataDeletionPage from "@/pages/legal/DataDeletionPage";
import LandingToggleGuard from "@/components/shared/LandingToggleGuard";
import PwaUpdateBanner from "@/components/shared/PwaUpdateBanner";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 mono">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={`/${user.role}`} replace />;
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 mono">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.open_school) return <Navigate to="/open" replace />;
  const path = user.role === "global_admin" ? "/global" : `/${user.role}`;
  return <Navigate to={path} replace />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL synchronous check for OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<GlobalLanding />} />
      <Route path="/school/:slug" element={<SchoolLanding />} />
      <Route path="/posts" element={<PostsPage />} />
      <Route path="/posts/:id" element={<PostDetailPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/onboard" element={<OnboardWizard />} />
      <Route path="/open-school/onboard" element={<OpenOnboardWizard />} />
      <Route path="/franchise/onboard" element={<FranchiseOnboard />} />
      <Route path="/open/*" element={
        <ProtectedRoute roles={["student", "admin"]}><OpenStudentConsole /></ProtectedRoute>
      } />
      <Route path="/vendor/onboard" element={<VendorOnboard />} />
      <Route path="/creator/onboard" element={<CreatorOnboard />} />
      <Route path="/store" element={<LandingToggleGuard kind="store" hash="#storez"><Storefront /></LandingToggleGuard>} />
      <Route path="/store/vendor/:id" element={<VendorPublic />} />
      <Route path="/tv" element={<LandingToggleGuard kind="tv" hash="#ponderr-tv"><LiveTV /></LandingToggleGuard>} />
      <Route path="/tv/channel/:id" element={<ChannelPublic />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/parent-access" element={<ParentAccess />} />
      <Route path="/parent-portal" element={<ParentPortal />} />
      <Route path="/dashboard" element={<RoleRedirect />} />
      <Route path="/global/*" element={
        <ProtectedRoute roles={["global_admin"]}><GlobalDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/educator/*" element={
        <ProtectedRoute roles={["educator", "admin"]}><EducatorDashboard /></ProtectedRoute>
      } />
      <Route path="/student/kit-store" element={
        <ProtectedRoute roles={["student", "admin"]}><StudentKitStore /></ProtectedRoute>
      } />
      <Route path="/student/ponderrtv" element={
        <ProtectedRoute roles={["student", "admin"]}><StudentPonderrTV /></ProtectedRoute>
      } />
      <Route path="/student/*" element={
        <ProtectedRoute roles={["student", "admin"]}><StudentDashboard /></ProtectedRoute>
      } />
      <Route path="/accountant/*" element={
        <ProtectedRoute roles={["accountant", "admin"]}><AccountantDashboard /></ProtectedRoute>
      } />
      <Route path="/founder/*" element={
        <ProtectedRoute roles={["founder", "admin"]}><FounderDashboard /></ProtectedRoute>
      } />
      <Route path="/facilitator/*" element={
        <ProtectedRoute roles={["facilitator", "admin"]}><FacilitatorDashboard /></ProtectedRoute>
      } />
      <Route path="/store_vendor/*" element={
        <ProtectedRoute roles={["store_vendor"]}><VendorDashboard /></ProtectedRoute>
      } />
      <Route path="/content_creator/*" element={
        <ProtectedRoute roles={["content_creator"]}><CreatorDashboard /></ProtectedRoute>
      } />
      <Route path="/pay/success" element={<PaySuccess />} />
      <Route path="/pay/cancel" element={<PayCancel />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <PwaUpdateBanner />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
